import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DialogModalDS,
  ButtonDS,
  Spacer,
  TextCapitalized,
  AccountBalanceOutlined,
} from "@qivia/ui";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  updateMemobankMandateStatusToActiveAsync,
  updateMemobankMandateStatusToBankApprovedAsync,
  updateMemobankMandateStatusToBankRequestedAsync,
} from "../sepaDirectDebitSlice";
import { selectAdminUser } from "../../homeSlice";

interface UpdateMandateStatusModalProps {
  onClose: () => void;
  isVisible: boolean;
  selectedMandateUuid: string | null;
  actionType: "bankRequested" | "bankApproved" | "active" | null;
}

export const UpdateMandateStatusModal = (
  props: UpdateMandateStatusModalProps,
) => {
  const { onClose, selectedMandateUuid, actionType, isVisible } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const adminUser = useAppSelector(selectAdminUser);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (selectedMandateUuid && adminUser && actionType) {
      if (actionType === "active") {
        void dispatch(
          updateMemobankMandateStatusToActiveAsync({
            mandateUuid: selectedMandateUuid,
            updatedBy: adminUser.email,
          }),
        );
      } else if (actionType === "bankRequested") {
        void dispatch(
          updateMemobankMandateStatusToBankRequestedAsync({
            mandateUuid: selectedMandateUuid,
            updatedBy: adminUser.email,
          }),
        );
      } else if (actionType === "bankApproved") {
        void dispatch(
          updateMemobankMandateStatusToBankApprovedAsync({
            mandateUuid: selectedMandateUuid,
            updatedBy: adminUser.email,
          }),
        );
      }
      onClose();
    }
  }, [selectedMandateUuid, onClose, dispatch, adminUser, actionType]);

  return (
    <DialogModalDS
      iconTitle={<AccountBalanceOutlined />}
      visible={isVisible}
      onClose={handleClose}
      title={t(`memobankMandates.modal.${actionType}.title`)}
      width={45}
      bottomButtons={{
        rightButton: (
          <ButtonDS
            text={t("memobankMandates.modal.confirm")}
            format="fill"
            buttonType="primary"
            onClick={handleConfirm}
          />
        ),
        leftButton: (
          <ButtonDS
            text={t("memobankMandates.modal.cancel")}
            format="fill"
            buttonType="secondary"
            onClick={handleClose}
          />
        ),
      }}
    >
      <StyledModalContent>
        <Spacer y={1} />
        <TextCapitalized>
          {t(`memobankMandates.modal.${actionType}.message`)}
        </TextCapitalized>
        <Spacer y={2} />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;
